import React from 'react';

import { useNotification } from '@/components/notification-system';
import { IconButton, useTheme } from '@/components/ui';

type DeleteButtonProps = {
	/**
	 * Callback function to execute when deletion is confirmed
	 */
	onDelete: () => Promise<void>;

	/**
	 * Title for the confirmation dialog
	 */
	title?: string;

	/**
	 * Message for the confirmation dialog
	 */
	message?: string;

	/**
	 * Size of the delete icon
	 */
	size?: number;

	/**
	 * Additional styles to apply to the button
	 */
	style?: any;

	/**
	 * Icon to use (defaults to 'delete')
	 */
	icon?: string;

	/**
	 * Whether the button is disabled
	 */
	disabled?: boolean;

	/**
	 * Text for the cancel button
	 */
	cancelText?: string;

	/**
	 * Text for the delete button
	 */
	deleteText?: string;

	/**
	 * Message to show on successful deletion
	 */
	successMessage?: string;

	testID?: string;
};

export const DeleteButton: React.FC<DeleteButtonProps> = ({
	onDelete,
	title = 'Delete Item',
	message = 'Are you sure you want to delete this item?',
	size = 16,
	style = { margin: 0 },
	icon = 'trash-outline',
	disabled = false,
	cancelText = 'Cancel',
	deleteText = 'Delete',
	successMessage,
	testID = 'btn-delete',
}) => {
	const theme = useTheme();
	const { showModal } = useNotification();

	const handlePress = () => {
		showModal({
			title,
			message,
			variant: 'error',
			actions: [
				{
					label: cancelText,
					style: 'default',
					onPress: () => {},
				},
				{
					label: deleteText,
					style: 'destructive',
					onPress: async () => {
						try {
							await onDelete();

							if (successMessage) {
								showModal({ title: 'Success', message: successMessage, variant: 'success' });
							}
						} catch (error) {
							console.error('Error during deletion:', error);
							showModal({ title: 'Error', message: 'Failed to delete the item', variant: 'error' });
						}
					},
				},
			],
		});
	};

	return (
		<IconButton
			icon={icon}
			size={size}
			style={style}
			iconColor={theme.colors.error}
			onPress={handlePress}
			disabled={disabled}
			testID={testID}
		/>
	);
};
